<template>
  <v-container fluid>
    <ActionBar
      :filters="filters"
      @change="updatePage"
    />

    <div class="page__content--no-items">
      <Filters
        :status="currentStatus"
        @change="updateFilters"
      />

      <Table
        :items="items"
        :pagination="{}"
        :status="currentStatus"
        :drawerVisibility="drawerVisibility"
        :cellPhone="isValidCellPhoneNumber"
        @reloadData="reloadData"
        @goToPage="updatePage"
      />
    </div>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import { isObject } from 'utils/functions'
import ActionBar from 'components/searchPhone/ActionBar'
import Filters from 'components/searchPhone/Filters'
import Table from 'components/searchPhone/Table'

export default {
  name: 'SearchPhonePage',
  components: {
    ActionBar,
    Filters,
    Table
  },
  computed: {
    isLoading () {
      return this.currentStatus === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.currentStatus === STATUS_SUCCESS
    },

    isFailed () {
      return this.currentStatus === STATUS_FAILED
    },

    isValidCellPhoneNumber () {
      return typeof this.filters.search === 'string' && this.filters.search.length === 11
    }
  },
  data () {
    return {
      namespace: 'Views.SearchPhonePage',
      pagination: {
        totalElements: 0,
        totalPages: 0,
        currentPage: 1,
        itemsPerPage: 25
      },
      filters: {
        search: null
      },
      currentStatus: STATUS_SUCCESS,
      items: [],
      drawerVisibility: true
    }
  },

  methods: {
    ...mapActions({
      showAlert: 'snackbar/show'
    }),

    isValidObject (val) {
      return isObject(val)
    },

    updatePagination (response, itemsPerPage) {
      const totalPagesCalc = Math.ceil(response.length / itemsPerPage)
      const totalPages = totalPagesCalc > 0 ? totalPagesCalc : 1

      this.pagination = {
        ...this.pagination,
        totalElements: response.length,
        totalPages
      }
    },

    resetPagination () {
      this.pagination = {
        totalElements: 0,
        totalPages: 0,
        currentPage: 1,
        itemsPerPage: 25
      }
    },

    updateFilters (filters) {
      this.filters = { ...filters }
      if (this.filters.search) {
        this.reloadData()
        return
      }

      this.items = []
    },

    updatePage (page) {
      scrollTo(0, 0)

      this.pagination = {
        ...this.pagination,
        currentPage: page
      }

      this.loadData()
    },

    mapPayload (res) {
      return res.data.employees
        .sort((a, b) => {
          if (a.status < b.status) { return 1 }
          if (a.status > b.status) { return -1 }
          return 0
        })
        .map((employee, index) => {
          const { phone, phoneCountry } = res.data
          const companyStatus = {
            boolean: /^(ativa|enabled)$/i.test(employee.companyStatus),
            text: /^(ativa|enabled)$/i.test(employee.companyStatus) ? 'Ativa' : employee.companyStatus
          }

          // COST CENTER
          const hasAllCostCenterBlocked = employee.costCenterList.every(costCenter => costCenter.blocked)
          const costCenterStatus = hasAllCostCenterBlocked ? 'disabled' : 'enabled'

          // TIME POLICY
          const hasAlltimePolicyNotApproved = employee.costCenterList.every(costCenter => !costCenter.timePolicyApproved)
          const timePolicyStatus = hasAlltimePolicyNotApproved ? 'disabled' : 'enabled'

          // CREDIT POLICY
          const ridesStatus = typeof employee.ridesAvaliable === 'number' && employee.ridesAvaliable <= 0
            ? 'disabled'
            : 'enabled'
          const budgetStatus = typeof employee.budgetAvaliable === 'number' && employee.budgetAvaliable <= 0
            ? 'disabled'
            : 'enabled'

          // VOUCHER STATUS
          let voucherStatus = null

          // VOUCHER AVAILABLE
          if (
            (
              this.isValidObject(employee.voucherAnalyze) &&
              employee.voucherAnalyze.voucherUS &&
              employee.voucherAnalyze.corporateVoucher
            ) &&
              companyStatus.boolean &&
              costCenterStatus === 'enabled' &&
              timePolicyStatus === 'enabled' &&
              ridesStatus === 'enabled' &&
              budgetStatus === 'enabled'
          ) {
            voucherStatus = 'available'
          }

          // VOUCHER UNAVAILABLE
          if (
            !companyStatus.boolean ||
            costCenterStatus === 'disabled' ||
            timePolicyStatus === 'disabled' ||
            ridesStatus === 'disabled' ||
            budgetStatus === 'disabled'
          ) {
            voucherStatus = 'unavailable'
          }

          // VOUCHER NOT SYNCHRONIZED
          if (
            this.isValidObject(employee.voucherAnalyze) &&
            (employee.voucherAnalyze.voucherUS === false || employee.voucherAnalyze.corporateVoucher === false)
          ) {
            voucherStatus = 'notSynchronized'
          }

          return {
            voucher: {
              status: voucherStatus,
              voucherAnalyze: employee.voucherAnalyze || null
            },
            employee: {
              id: employee.id,
              status: employee.status,
              name: employee.name,
              phone: {
                number: phone,
                country: phoneCountry
              },
              categories: employee.categories
            },
            company: {
              id: employee.companyID,
              status: companyStatus,
              name: employee.companyName,
              cnpj: employee.cnpj
            },
            costCenter: {
              status: costCenterStatus,
              list: employee.costCenterList
            },
            timePolicy: {
              status: timePolicyStatus
            },
            rides: {
              status: ridesStatus,
              available: employee.ridesAvaliable
            },
            budget: {
              status: budgetStatus,
              available: employee.budgetAvaliable
            }
          }
        })
    },

    async loadData () {
      this.currentStatus = STATUS_IS_LOADING

      try {
        const { data: res } = await this.$apiService.searchEmployeePhone(this.filters.search)
        this.items = this.mapPayload(res)
        this.currentStatus = STATUS_SUCCESS
      } catch (err) {
        console.log('ERROR:', err)
        this.currentStatus = STATUS_FAILED
      }
    },

    reloadData () {
      this.resetPagination()
      this.loadData()
    }
  },

  watch: {
    selectedCompany () {
      this.loadData()
    },

    'filters.search' () {
      this.drawerVisibility = !this.drawerVisibility
    }
  }
}
</script>
